import { CheckIcon } from '../../../../assets/CheckIcon';

interface FilterChipProps {
  /** The stored filter value. Also the visible text unless `label` overrides it. */
  value: string;
  /**
   * What the coach reads, when that differs from the stored value.
   *
   * The scalar facets store wire values a coach would not recognise — Level stores the
   * ordinal `'1'`, not `'JUCO'` — so the chip needs a display name separate from the
   * value it toggles. Grad Year and tag options pass no label because for them the two
   * are the same string.
   */
  label?: string;
  isSelected: boolean;
  onClick: () => void;
  isSingleSelect: boolean;
  /**
   * Renders the chip inert and announces it as disabled.
   *
   * Used where a facet cannot produce results in the current selection (JC Year without
   * Level = JUCO). The chip stays in the DOM with an explanation beside it rather than
   * disappearing, so the coach can see why it is unavailable.
   *
   * `aria-disabled` rather than the native `disabled` attribute: a natively disabled
   * button drops out of the tab order entirely, so a keyboard or screen reader user
   * never learns the facet exists. This keeps it reachable and announced as unavailable,
   * with the click suppressed here.
   */
  disabled?: boolean;
}

export const FilterChip = ({
  value,
  label,
  isSelected,
  onClick,
  isSingleSelect,
  disabled = false,
}: FilterChipProps) => {
  const text = label ?? value;

  return (
    <button
      type="button"
      className={`filter-chip ${isSelected ? 'filter-chip--selected' : ''} ${
        disabled ? 'filter-chip--disabled' : ''
      }`}
      onClick={disabled ? undefined : onClick}
      role={isSingleSelect ? 'radio' : 'checkbox'}
      aria-checked={isSelected}
      aria-label={text}
      aria-disabled={disabled || undefined}
    >
      <span className="filter-chip__text">{text}</span>
      {isSelected && (
        <CheckIcon className="filter-chip__checkmark" />
      )}
    </button>
  );
};
